"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { LANG_STORAGE_KEY, detectLocale, translate, type Locale } from "@/lib/i18n";

type I18nContextValue = { locale: Locale; setLocale: (locale: Locale) => void };

const I18nContext = createContext<I18nContextValue>({ locale: "fr", setLocale: () => {} });

export const useI18n = () => useContext(I18nContext);

const ATTRIBUTES = ["placeholder", "aria-label", "alt", "title"] as const;
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE"]);

// Texte source (français) et dernière valeur écrite, par nœud / attribut
type Entry = { source: string; applied: string };
const textEntries = new WeakMap<Text, Entry>();
const attrEntries = new WeakMap<Element, Map<string, Entry>>();

function resolve(entry: Entry | undefined, current: string, locale: Locale): Entry {
  // Si React a remplacé la valeur depuis notre passage, elle devient la nouvelle source
  const source = entry && entry.applied === current ? entry.source : current;
  const match = translate(source, locale);
  if (!match) {
    if (process.env.NODE_ENV === "development" && locale !== "fr" && /[a-zà-ÿ]{2}/i.test(source)) {
      const w = window as unknown as { __i18nMissing?: Set<string> };
      (w.__i18nMissing ??= new Set()).add(source.trim());
    }
    return { source, applied: source };
  }
  const lead = source.match(/^\s*/)?.[0] ?? "";
  const trail = source.match(/\s*$/)?.[0] ?? "";
  return { source, applied: lead + match + trail };
}

function isSkipped(el: Element | null) {
  return !!el?.closest("[translate='no'], [data-no-translate]") || (!!el && SKIP_TAGS.has(el.tagName));
}

function translateText(node: Text, locale: Locale) {
  if (!node.nodeValue?.trim() || isSkipped(node.parentElement)) return;
  const next = resolve(textEntries.get(node), node.nodeValue, locale);
  textEntries.set(node, next);
  if (node.nodeValue !== next.applied) node.nodeValue = next.applied;
}

function translateAttributes(el: Element, locale: Locale) {
  if (isSkipped(el)) return;
  for (const name of ATTRIBUTES) {
    const current = el.getAttribute(name);
    if (!current) continue;
    let map = attrEntries.get(el);
    if (!map) attrEntries.set(el, (map = new Map()));
    const next = resolve(map.get(name), current, locale);
    map.set(name, next);
    if (current !== next.applied) el.setAttribute(name, next.applied);
  }
}

// Titre d'onglet et balises meta (description, Open Graph, Twitter)
const HEAD_META = "meta[name='description'], meta[property='og:title'], meta[property='og:description'], meta[name='twitter:title'], meta[name='twitter:description']";
let titleEntry: Entry | undefined;

function translateHead(locale: Locale) {
  const next = resolve(titleEntry, document.title, locale);
  titleEntry = next;
  if (document.title !== next.applied) document.title = next.applied;
  document.head.querySelectorAll(HEAD_META).forEach((meta) => {
    const current = meta.getAttribute("content");
    if (!current) return;
    let map = attrEntries.get(meta);
    if (!map) attrEntries.set(meta, (map = new Map()));
    const entry = resolve(map.get("content"), current, locale);
    map.set("content", entry);
    if (current !== entry.applied) meta.setAttribute("content", entry.applied);
  });
}

function translateTree(root: Node, locale: Locale) {
  if (root.nodeType === Node.TEXT_NODE) return translateText(root as Text, locale);
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  translateAttributes(root as Element, locale);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) translateText(n as Text, locale);
    else translateAttributes(n as Element, locale);
  }
}

export default function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("fr");

  useEffect(() => {
    setLocaleState(detectLocale());
  }, []);

  const setLocale = useCallback((next: Locale) => {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // no-op
    }
    setLocaleState(next);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    html.lang = locale;
    translateTree(document.body, locale);
    translateHead(locale);
    html.classList.remove("cp-i18n-pending");

    const headObserver = new MutationObserver(() => translateHead(locale));
    headObserver.observe(document.head, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["content"] });

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") translateText(m.target as Text, locale);
        else if (m.type === "attributes") translateAttributes(m.target as Element, locale);
        else m.addedNodes.forEach((n) => translateTree(n, locale));
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...ATTRIBUTES],
    });
    return () => {
      observer.disconnect();
      headObserver.disconnect();
    };
  }, [locale]);

  return <I18nContext.Provider value={{ locale, setLocale }}>{children}</I18nContext.Provider>;
}
